
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useReportMatching = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const triggerReportMatching = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found for report matching');
        return;
      }

      console.log('🎯 Triggering enhanced radius-based report matching for user:', user.id);

      const { error } = await supabase.rpc('match_reports_to_profile', {
        profile_user_id: user.id
      });

      if (error) {
        console.error('Error in enhanced match_reports_to_profile RPC:', error);
        throw error;
      }

      console.log('✅ Enhanced radius-based report matching completed successfully');
    } catch (err) {
      console.error("Error triggering enhanced report matching:", err);
      setError('Failed to match reports with enhanced radius-based matching');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fixNullMarketData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔧 Fixing NULL market_types and report_type data with enhanced location parsing...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user found');
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) {
        console.log('No profile found');
        return;
      }

      // Get profile reports and their associated USDA reports separately
      const { data: profileReports, error: fetchError } = await supabase
        .from("profile_reports")
        .select("id, report_id")
        .eq("profile_id", profile.id);

      if (fetchError) {
        console.error('Error fetching profile reports:', fetchError);
        return;
      }

      console.log(`Found ${profileReports?.length || 0} profile reports`);

      if (!profileReports || profileReports.length === 0) {
        return;
      }

      // Get USDA report details for the profile reports
      const reportIds = profileReports.map(pr => pr.report_id);
      const { data: usdaReports } = await supabase
        .from("usda_reports")
        .select("id, market_types, report_type, location_type, location_name, latitude, longitude")
        .in("id", reportIds);

      const usdaReportsMap = new Map();
      usdaReports?.forEach(report => {
        usdaReportsMap.set(report.id, report);
      });

      for (const profileReport of profileReports) {
        const usdaReport = usdaReportsMap.get(profileReport.report_id);
        if (!usdaReport) continue;

        const { error: updateError } = await supabase
          .from("profile_reports")
          .update({
            market_types: usdaReport.market_types,
            report_type: usdaReport.report_type
          })
          .eq("id", profileReport.id);

        if (updateError) {
          console.error(`Error updating profile report ${profileReport.id}:`, updateError);
        } else {
          const coords = usdaReport.latitude && usdaReport.longitude 
            ? `(${usdaReport.latitude}, ${usdaReport.longitude})`
            : 'No coordinates';
          console.log(`✅ Updated profile report ${profileReport.id} with enhanced data (Type: ${usdaReport.location_type}, Name: ${usdaReport.location_name}, Coords: ${coords})`);
        }
      }

      console.log('🎉 Finished fixing NULL market data with enhanced radius-based location matching');
    } catch (err) {
      console.error("Error fixing NULL market data:", err);
      setError('Failed to fix NULL market data with enhanced matching');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    triggerReportMatching,
    fixNullMarketData,
    loading,
    error
  };
};
